import { ContainerErrorType } from '@fluidframework/container-definitions';
import { DriverErrorType } from '@fluidframework/driver-definitions';
import { OdspErrorType } from '@fluidframework/odsp-driver-definitions';
import { OWHErrorType } from '@fluidx/office-fluid-types';
import {
  tryMatchContainerErrorType,
  tryMatchDriverErrorType,
  tryMatchOdspDriverErrorType,
  tryMatchOWHErrorType,
  Locale
} from '@fluidx/utilities';
import { getStringTable } from '../../../utilities/localizationUtilities';
import { HostUxStrings } from '../../../stringTypes/HostUxStrings';
import { FluidErrorInfo, UnknownError } from '../ErrorInfo';
import { ErrorDialogStrings } from './ErrorDialog';

export interface DialogErrorInfo extends FluidErrorInfo {
  dialogStrings: ErrorDialogStrings;
}

/**
 * Inspects a given error to determine potential fluid error type and
 * select appropriate localized string for dialog error ux
 * @param error driver, container or office web host error
 * @param locale Host locale
 * @returns Promise to DialogErrorInfo
 */
export async function getDialogMessagingForError(error: unknown, locale: Locale): Promise<DialogErrorInfo> {
  const stringTable = await getStringTable(locale);

  const defaultDialogErrorInfo: DialogErrorInfo = {
    errorType: UnknownError,
    dialogStrings: {
      dialogTitle: stringTable.genericErrorDialogTitle,
      dialogMessage: stringTable.genericErrorMessage
    }
  };

  const dialogErrorInfo: DialogErrorInfo =
    getDialogMessagingForContainerError(error, stringTable) ||
    getDialogMessagingForBaseDriverError(error, stringTable) ||
    getDialogMessagingForOdspDriverError(error, stringTable) ||
    defaultDialogErrorInfo;

  return Promise.resolve(dialogErrorInfo);
}

/**
 * Select appropriate localized string for container errors
 */
function getDialogMessagingForContainerError(error: unknown, stringTable: HostUxStrings): DialogErrorInfo | undefined {
  const errorType = tryMatchContainerErrorType(error) || tryMatchOWHErrorType(error);

  if (!errorType) {
    return;
  }

  switch (errorType) {
    case OWHErrorType.ContainerUpgradeRequired:
      return {
        errorType,
        dialogStrings: {
          dialogTitle: stringTable.containerUpgradeRequiredTitle,
          dialogMessage: stringTable.containerUpgradeRequiredMessage
        }
      };

    case OWHErrorType.ComponentNotFound:
      return {
        errorType,
        dialogStrings: {
          // TODO: Make sure these are the redlines strings we need to use.
          // Remove this line or update once the tracking item from the Content team is resolved.
          // https://office.visualstudio.com/DefaultCollection/MAX/_workitems/edit/5063009
          dialogTitle: stringTable.componentNotFoundTitle,
          dialogMessage: stringTable.componentNotFoundMessage
        }
      };

    case ContainerErrorType.throttlingError:
      return {
        errorType,
        dialogStrings: {
          dialogTitle: stringTable.genericServiceBusyDialogTitle,
          dialogMessage: stringTable.genericServiceBusyMessage
        }
      };

    case ContainerErrorType.genericError:
      return {
        errorType,
        dialogStrings: {
          dialogTitle: stringTable.genericErrorDialogTitle,
          dialogMessage: stringTable.genericErrorMessage
        }
      };

    default:
      return;
  }
}

/**
 * Select appropriate localized string for base driver errors
 */
function getDialogMessagingForBaseDriverError(error: unknown, stringTable: HostUxStrings): DialogErrorInfo | undefined {
  const errorType = tryMatchDriverErrorType(error);

  if (!errorType) {
    return;
  }

  switch (errorType) {
    case DriverErrorType.fileNotFoundOrAccessDeniedError:
      return {
        errorType,
        dialogStrings: {
          dialogTitle: stringTable.fileNotFoundOrAccessDeniedTitle,
          dialogMessage: stringTable.fileNotFoundOrAccessDeniedMessage
        }
      };

    case DriverErrorType.authorizationError:
      return {
        errorType,
        dialogStrings: {
          dialogTitle: stringTable.authorizationErrorTitle,
          dialogMessage: stringTable.authorizationErrorMessage
        }
      };

    case DriverErrorType.throttlingError:
      return {
        errorType,
        dialogStrings: {
          dialogTitle: stringTable.genericServiceBusyDialogTitle,
          dialogMessage: stringTable.genericServiceBusyMessage
        }
      };

    case DriverErrorType.genericError:
      return {
        errorType,
        dialogStrings: {
          dialogTitle: stringTable.genericErrorDialogTitle,
          dialogMessage: stringTable.genericErrorMessage
        }
      };

    default:
      return;
  }
}

/**
 * Select appropriate localized string for Odsp specific driver errors
 */
function getDialogMessagingForOdspDriverError(error: unknown, stringTable: HostUxStrings): DialogErrorInfo | undefined {
  const errorType = tryMatchOdspDriverErrorType(error);

  if (!errorType) {
    return;
  }

  switch (errorType) {
    case DriverErrorType.fileOverwrittenInStorage:
      // TODO: Task 4663170: Show specialized dialogs for ODSP errors?
      return {
        errorType,
        dialogStrings: {
          dialogTitle: stringTable.odspVersionMismatchBannerTitle,
          dialogMessage: stringTable.odspVersionMismatchBannerMessage
        }
      };

    case OdspErrorType.outOfStorageError:
      // TODO: Task 4663170: Show specialized dialogs for ODSP errors?
      return {
        errorType,
        dialogStrings: {
          dialogTitle: stringTable.odspOutOfStorageDialogTitle,
          dialogMessage: stringTable.odspOutOfStorageMessage
        }
      };

    // TBD: Define error messaging for these Odsp error cases
    case OdspErrorType.invalidFileNameError:
    case OdspErrorType.snapshotTooBig:
    case OdspErrorType.fetchTimeout:
    case OdspErrorType.fluidNotEnabled:

    default:
      return;
  }
}
